import { Injectable } from '@angular/core';

import { Observable, BehaviorSubject } from 'rxjs';

import { State, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { tap, map } from 'rxjs/operators';

import { GridDataResult } from '@progress/kendo-angular-grid';
import { Params } from '@angular/router';

import * as moment from 'moment';

import { IncidentsQualityAnalysisService } from '@libs/portal-common/services';
import { AppNotificationsService } from '@libs/portal-common/system';

const EMPTY_RESULT: GridDataResult = {
  data: [],
  total: 0,
};

export class QualityAnalysisFilter {
  Start: Date = new Date(new Date().setHours(0, 0, 0, 0));
  End: Date = new Date(new Date().setHours(23, 59, 0, 0));

  ContactId?: number;
  IncidentId?: number;
  TemplateId?: number;
  LocationId?: number;
}

@Injectable()
export class QualityAnalysisService extends BehaviorSubject<GridDataResult> {
  public busy$ = new BehaviorSubject(false);

  constructor(
    private incidentsQAService: IncidentsQualityAnalysisService,
    private notifications: AppNotificationsService,
  ) {
    super(null);
  }

  public query(filter: QualityAnalysisFilter, state: State) {
    state.sort = state.sort.filter((sortDescriptor) => sortDescriptor.dir === 'asc' || sortDescriptor.dir === 'desc');

    this.busy$.next(true);

    this.getQA(filter, state).subscribe(
      (x) => {
        this.busy$.next(false);
        super.next(x);
      },
      (err) => {
        this.busy$.next(false);
        super.next(EMPTY_RESULT);
        this.notifications.error(err);
      },
    );
  }

  public clear(): void {
    super.next(EMPTY_RESULT);
  }

  getQA(filter: QualityAnalysisFilter, state: State): Observable<GridDataResult> {
    let qaFilter = this.constructFilter(filter);

    let params = {
      group: state.group,
      filter: <CompositeFilterDescriptor>{
        logic: 'and',
        filters: [qaFilter],
      },
      skip: state.skip,
      limit: state.take,
      sort: state.sort,
    };

    if (state.filter.filters.length > 0) {
      params.filter.filters.push(state.filter);
    }

    return this.incidentsQAService.getAllPost(null, params).pipe(
      tap((response) => {
        response.Data.forEach((element) => {
          element.IncidentQualityAnalysisAnswersDict = element.IncidentQualityAnalysisAnswers.reduce(
            (a, x) => ({ ...a, [x.fkQualityAnalysisQuestion]: x.Answer }),
            {},
          );
        });
        this.notifications.checkResponse(response);
      }),
      map(
        (response) =>
          <GridDataResult>{
            data: response.Groups || response.Data || [],
            total: response.Total,
          },
      ),
    );
  }

  private constructFilter(filter: QualityAnalysisFilter): CompositeFilterDescriptor {
    let qaFilter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [],
    };

    if (filter.IncidentId) {
      qaFilter.filters.push({ field: 'fkIncident', operator: 'eq', value: filter.IncidentId });
      return qaFilter;
    }

    if (filter.Start) {
      qaFilter.filters.push({ field: 'Created', operator: 'gt', value: filter.Start });
    }

    if (filter.End) {
      qaFilter.filters.push({ field: 'Created', operator: 'lt', value: filter.End });
    }

    if (filter.ContactId) {
      qaFilter.filters.push({ field: 'fkAgent', operator: 'eq', value: filter.ContactId });
    }

    if (filter.TemplateId) {
      qaFilter.filters.push({ field: 'fkIncidentQualityAnalysisTemplate', operator: 'eq', value: filter.TemplateId });
    }

    if (filter.LocationId) {
      qaFilter.filters.push({ field: 'fkLot', operator: 'eq', value: filter.LocationId });
    }

    return qaFilter;
  }
}
